import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Trash2, AlertCircle, ArrowRight, Pencil } from 'lucide-react'
import { T } from '../theme'
import { parseAnnotationSets } from '../utils/annotationSets'

const D     = '#1a1916'
const GOLD  = '#4F8EF7'
const CREAM = '#f5f2eb'
const INK   = '#1a1916'
const MUTED = 'rgba(26,25,22,0.45)'
const RULE  = 'rgba(26,25,22,0.09)'
const RED   = '#ef4444'
const ORANGE = '#f59e0b'
const MONO  = T.mono

const MOMENT_TYPES = [
  { value: 'goal',                label: 'But',                  color: '#4F8EF7' },
  { value: 'chance',              label: 'Occasion',             color: '#f59e0b' },
  { value: 'loss',                label: 'Perte de balle',       color: '#dc2626' },
  { value: 'recovery',            label: 'Recuperation',         color: '#16a34a' },
  { value: 'transition_for',      label: 'Transition off.',      color: '#3b82f6' },
  { value: 'transition_against',  label: 'Transition subie',     color: '#991b1b' },
  { value: 'defensive_phase',     label: 'Phase defensive',      color: '#6b7280' },
  { value: 'set_piece',           label: 'Coup de pied arrete',  color: '#a855f7' },
  { value: 'tactical_note',       label: 'Note tactique',        color: '#9ca3af' },
  { value: 'player_action',       label: 'Geste joueur',         color: '#6366f1' },
]

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00'
  const total = Math.floor(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  if (h > 0) return `${String(h).padStart(2, '0')}:${mm}:${ss}`
  return `${mm}:${ss}`
}

// ─── Resilience reseau ──────────────────────────────────────────────────
// Cold starts Render : la 1ere requete apres ~15min d'idle ou apres un
// push backend peut echouer en TypeError "Failed to fetch" pendant 5-15s
// le temps que l'instance redemarre. Au lieu d'afficher cette erreur a
// l'user, on retente silencieusement avec backoff exponentiel.

function isNetworkError(e) {
  return e instanceof TypeError && /fetch|network/i.test(e.message || '')
}

async function runWithRetry(fn, retries = 3, baseDelay = 1000) {
  let lastErr
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn()
    } catch (e) {
      lastErr = e
      if (i === retries || !isNetworkError(e)) throw e
      await new Promise(r => setTimeout(r, baseDelay * Math.pow(2, i)))
    }
  }
  throw lastErr
}

function friendlyError(e, fallback) {
  if (isNetworkError(e)) {
    return 'Connexion temporairement perdue. Réessaie dans quelques instants.'
  }
  return e?.message || fallback
}

/**
 * Modale de marquage / edition d'un moment (bookmark simple).
 *
 * Depuis la refonte du flow Studio, l'extraction de clip est gérée par
 * CreateClipModal (CTA "Découper un clip" du header). Cette modale ne fait
 * plus que le bookmark : type, joueurs, note.
 *
 * Les annotations ne sont plus editees ici : le bouton "Annoter" delegue a
 * AnnotationOverlay (overlay SVG sur la frame figee), via onAnnotate. Cette
 * modale ne lit ni n'ecrit plus moment.annotations — c'etait le chemin par
 * lequel l'ancien editeur pouvait ecraser le tableau avec ce qu'il savait
 * charger.
 */
export default function MomentMarker({
  moment = null,
  timecode = 0,
  players = [],
  matchId = null,
  onSave,
  onDelete,
  onAnnotate,
  onClose,
}) {
  const navigate = useNavigate()
  const isEdit = !!moment

  // La lineup persistee en BD a la forme { player_id, name, number, position, starter }.
  // On normalise ici pour que le reste du composant manipule
  // { id, name, number, position, is_starter } sans id undefined.
  const normalizedPlayers = useMemo(() => (
    (players || [])
      .map(p => ({
        ...p,
        id: p.id ?? p.player_id ?? null,
        is_starter: p.is_starter ?? p.starter ?? false,
      }))
      .filter(p => p.id != null)
  ), [players])

  const hasLineup = normalizedPlayers.length > 0

  const [type, setType]           = useState(moment?.moment_type || 'tactical_note')
  const [playerIds, setPlayerIds] = useState(moment?.player_ids || [])
  const [note, setNote]           = useState(moment?.note || '')
  const [saving, setSaving]       = useState(false)
  const [deleting, setDeleting]   = useState(false)
  const [error, setError]         = useState(null)

  // Nombre de points d'arret annotes sur ce moment, pour le libelle du bouton.
  // Les entrees au format herite ne sont pas comptees : elles ne sont pas
  // affichables par l'overlay.
  const annotationSetCount = useMemo(
    () => parseAnnotationSets(moment?.annotations).length,
    [moment],
  )

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose && onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const togglePlayer = (id) => {
    setPlayerIds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id])
  }

  const handleSave = async () => {
    setError(null)
    setSaving(true)
    try {
      await runWithRetry(() => onSave({
        moment_type: type,
        player_ids: playerIds,
        note: note.trim() || null,
        ...(isEdit ? {} : { timecode_start: timecode }),
      }))
      onClose && onClose()
    } catch (e) {
      setError(friendlyError(e, 'Erreur lors de la sauvegarde'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!isEdit || !onDelete) return
    if (!window.confirm('Supprimer ce moment ?')) return
    setDeleting(true)
    try {
      await runWithRetry(() => onDelete())
      onClose && onClose()
    } catch (e) {
      setError(friendlyError(e, 'Erreur lors de la suppression'))
      setDeleting(false)
    }
  }

  const goToMatchDetail = () => {
    onClose && onClose()
    navigate(`/dashboard/matches/${matchId}`)
  }

  const sortedPlayers = [...normalizedPlayers].sort((a, b) => {
    const aIsStarter = a.is_starter ? 1 : 0
    const bIsStarter = b.is_starter ? 1 : 0
    if (aIsStarter !== bIsStarter) return bIsStarter - aIsStarter
    return (a.number || 99) - (b.number || 99)
  })

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(10,9,8,0.7)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: CREAM,
          width: '100%', maxWidth: 560,
          maxHeight: '90vh', overflowY: 'auto',
          borderTop: `2px solid ${GOLD}`,
        }}
      >
        <div style={{
          padding: '18px 22px',
          borderBottom: `1px solid ${RULE}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <p style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.18em', textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>
              {isEdit ? 'Editer le moment' : 'Marquer un moment'}
            </p>
            <p style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: INK, letterSpacing: '.04em' }}>
              {formatTime(isEdit ? moment.timecode_start : timecode)}
            </p>
          </div>
          <button onClick={onClose} aria-label="Fermer" style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: MUTED, padding: 4,
          }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: '20px 22px' }}>

          {!hasLineup && (
            <div style={{
              background: 'rgba(245,158,11,0.06)',
              border: `1px solid rgba(245,158,11,0.25)`,
              borderLeft: `3px solid ${ORANGE}`,
              padding: '12px 14px',
              marginBottom: 18,
              display: 'flex', alignItems: 'center', gap: 12,
              flexWrap: 'wrap',
            }}>
              <AlertCircle size={16} color="#b45309" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 200 }}>
                <p style={{ fontSize: 12, color: INK, lineHeight: 1.5, margin: 0 }}>
                  Pas encore de composition saisie. Tu peux quand meme creer ce moment, mais sans tagger de joueur.
                </p>
              </div>
              <button
                onClick={goToMatchDetail}
                style={{
                  padding: '7px 12px',
                  background: ORANGE, border: 'none', color: INK,
                  fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                  whiteSpace: 'nowrap',
                }}
              >
                Saisir la composition <ArrowRight size={11} />
              </button>
            </div>
          )}

          {/* Type */}
          <p style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.18em', textTransform: 'uppercase', color: MUTED, marginBottom: 10 }}>
            Type
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, marginBottom: 18 }}>
            {MOMENT_TYPES.map(t => {
              const active = type === t.value
              return (
                <button
                  key={t.value}
                  onClick={() => setType(t.value)}
                  style={{
                    padding: '8px 10px',
                    background: active ? t.color : 'rgba(26,25,22,0.04)',
                    border: `1px solid ${active ? t.color : RULE}`,
                    color: active ? '#fff' : INK,
                    fontFamily: MONO, fontSize: 10,
                    letterSpacing: '.04em',
                    cursor: 'pointer',
                    transition: 'all .12s',
                    textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <span style={{
                    width: 8, height: 8, background: active ? '#fff' : t.color,
                    flexShrink: 0,
                  }} />
                  {t.label}
                </button>
              )
            })}
          </div>

          {/* Joueurs */}
          {hasLineup && (
            <>
              <p style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.18em', textTransform: 'uppercase', color: MUTED, marginBottom: 10 }}>
                Joueurs concernes (optionnel)
              </p>
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 5,
                marginBottom: 18, maxHeight: 140, overflowY: 'auto',
                padding: 8, border: `1px solid ${RULE}`, background: '#fff',
              }}>
                {sortedPlayers.map(p => {
                  const active = playerIds.includes(p.id)
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePlayer(p.id)}
                      style={{
                        padding: '6px 10px',
                        background: active ? GOLD : '#fff',
                        border: `1px solid ${active ? GOLD : RULE}`,
                        color: INK,
                        fontFamily: MONO, fontSize: 10,
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 6,
                        opacity: !active && p.is_starter === false ? 0.6 : 1,
                        transition: 'all .12s',
                      }}
                    >
                      {p.number != null && (
                        <span style={{
                          fontWeight: 700, minWidth: 14, textAlign: 'center',
                          color: INK,
                        }}>{p.number}</span>
                      )}
                      <span>{p.name}</span>
                    </button>
                  )
                })}
              </div>
            </>
          )}

          {/* Note */}
          <p style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.18em', textTransform: 'uppercase', color: MUTED, marginBottom: 10 }}>
            Note (optionnel)
          </p>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Ce que tu veux retenir de ce moment..."
            rows={3}
            style={{
              width: '100%', padding: '10px 12px',
              background: '#fff', border: `1px solid ${RULE}`,
              fontFamily: MONO, fontSize: 11, color: INK,
              outline: 'none', resize: 'vertical',
              boxSizing: 'border-box', marginBottom: 18,
            }}
          />

          {/* Action Annoter — quitte la modale et ouvre l'overlay SVG sur la
              frame du moment. Les deux ne peuvent pas cohabiter (la modale est
              en fixed plein ecran par-dessus le player), donc une saisie de
              type/note non enregistree est perdue : le bouton le dit. */}
          {isEdit && (
            <div style={{ marginBottom: 18 }}>
              <button
                onClick={() => onAnnotate && onAnnotate(moment)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: 'rgba(79,142,247,0.08)',
                  border: `1px solid rgba(79,142,247,0.3)`,
                  color: '#7a6219',
                  fontFamily: MONO, fontSize: 10, fontWeight: 700,
                  letterSpacing: '.1em', textTransform: 'uppercase',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <Pencil size={12} />
                {annotationSetCount > 0
                  ? `Annoter la frame (${annotationSetCount})`
                  : 'Annoter la frame'}
              </button>
              <p style={{
                fontFamily: MONO, fontSize: 9, color: MUTED,
                margin: '6px 0 0', lineHeight: 1.5, textAlign: 'center',
              }}>
                Ferme cette fenêtre et ouvre le mode annotation sur la frame du moment.
              </p>
            </div>
          )}

          {error && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(239,68,68,0.06)',
              border: `1px solid rgba(239,68,68,0.25)`,
              color: '#991b1b',
              fontFamily: MONO, fontSize: 11,
              marginBottom: 14,
            }}>
              {error}
            </div>
          )}
        </div>

        <div style={{
          padding: '14px 22px',
          borderTop: `1px solid ${RULE}`,
          background: 'rgba(26,25,22,0.02)',
          display: 'flex', gap: 8, justifyContent: 'space-between',
        }}>
          <div>
            {isEdit && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: '9px 13px',
                  background: 'transparent', border: `1px solid ${RULE}`,
                  color: RED,
                  fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Trash2 size={11} />
                {deleting ? 'Suppression...' : 'Supprimer'}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                padding: '9px 14px',
                background: 'transparent', border: `1px solid ${RULE}`,
                color: INK,
                fontFamily: MONO, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '9px 16px',
                background: GOLD, border: 'none', color: INK,
                fontFamily: MONO, fontSize: 9, fontWeight: 700,
                letterSpacing: '.12em', textTransform: 'uppercase',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Sauvegarde...' : isEdit ? 'Mettre a jour' : 'Creer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
